/* ========================================================================== 
   SALE MADRID — CART DRAWER GUARD

   Guarantees that every JavaScript-enabled add-to-cart form stays on the
   current page and opens the cart drawer. Native Shopify forms remain valid
   for no-JavaScript visitors.

   The normal theme handlers still run first. This file only intercepts a
   submission when no other handler has called preventDefault(), which makes
   it a safety net for custom sections, app-rendered cards and elements that
   were inserted before their component JavaScript finished initialising.
   ========================================================================== */
(function () {
  'use strict';

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function normalisePath(path) {
    return String(path || '')
      .replace(/\.js$/i, '')
      .replace(/\/$/, '');
  }

  function isCartAddForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;

    try {
      var action = new URL(form.getAttribute('action') || form.action || '', window.location.origin);
      var configuredAction = new URL(
        (window.routes && window.routes.cart_add_url) || '/cart/add.js',
        window.location.origin
      );
      var actionPath = normalisePath(action.pathname);
      var configuredPath = normalisePath(configuredAction.pathname);

      return actionPath === configuredPath || /\/cart\/add$/i.test(actionPath);
    } catch (error) {
      return false;
    }
  }

  function sectionIds(drawer) {
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];

    return drawer
      .getSectionsToRender()
      .map(function (section) {
        return section && section.id;
      })
      .filter(Boolean);
  }

  async function fetchFreshSections(drawer) {
    var ids = sectionIds(drawer);
    if (!ids.length) return null;

    var url = new URL(window.location.href);
    url.hash = '';
    url.search = '';
    url.searchParams.set('sections', ids.join(','));

    try {
      var response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) return null;
      return { sections: await response.json() };
    } catch (error) {
      console.error('Sale Madrid cart drawer: unable to refresh drawer sections.', error);
      return null;
    }
  }

  function openDrawer(drawer, trigger) {
    if (!drawer) return false;


    if (trigger && typeof drawer.setActiveElement === 'function') {
      drawer.setActiveElement(trigger);
    }

    if (typeof drawer.open === 'function') {
      drawer.open(trigger);
      return true;
    }

    var cartButton = document.querySelector('.cart-icon--drawer');
    if (cartButton) {
      cartButton.click();
      return true;
    }

    return false;
  }

  async function refreshAndOpen(payload, trigger) {
    var drawer = getDrawer();
    if (!drawer) return false;

    var state = payload && payload.sections ? payload : await fetchFreshSections(drawer);
    var rendered = false;

    if (state && state.sections && typeof drawer.renderContents === 'function') {
      try {
        if (trigger && typeof drawer.setActiveElement === 'function') {
          drawer.setActiveElement(trigger);
        }
        drawer.renderContents(state);
        rendered = true;
      } catch (error) {
        console.error('Sale Madrid cart drawer: unable to render returned sections.', error);
      }
    }

    if (!rendered) { window.location.assign((window.Shopify?.routes?.root || '/') + 'cart'); return true; }

    /* renderContents() opens the drawer itself. This delayed check covers
       third-party overrides that update the HTML but omit the open call. */
    window.setTimeout(function () {
      if (!drawer.classList.contains('active') && !drawer.classList.contains('open')) {
        openDrawer(drawer, trigger);
      }
    }, 100);

    return true;
  }

  function setLoading(button, loading) {
    if (!button) return;

    button.classList.toggle('loading', loading);
    button.toggleAttribute('aria-disabled', loading);
    button.toggleAttribute('aria-busy', loading);

    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  function showFormError(form, message) {
    var owner = form.closest('product-form-component, product-form, .quick-add, .sm-add') || form.parentElement;
    var wrapper = owner && owner.querySelector('.product-form__error-message-wrapper');
    var text = wrapper && wrapper.querySelector('.product-form__error-message');

    if (wrapper && text) {
      text.textContent = message;
      wrapper.removeAttribute('hidden');
      return;
    }

    var error = form.querySelector('[data-sm-cart-error]');
    if (!error) { error = document.createElement('p'); error.dataset.smCartError = ''; error.setAttribute('role', 'alert'); form.appendChild(error); }
    error.textContent = message;
  }

  async function handleUnhandledCartForm(event) {
    if (event.defaultPrevented) return;

    var form = event.target;
    if (!isCartAddForm(form)) return;

    event.preventDefault();

    var submitter = event.submitter || form.querySelector('[type="submit"], button:not([type])');
    if (submitter && (submitter.disabled || submitter.getAttribute('aria-disabled') === 'true')) return;

    var drawer = getDrawer();
    var formData = new FormData(form);
    var ids = sectionIds(drawer);

    if (ids.length && !formData.has('sections')) {
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    setLoading(submitter, true);

    try {
      var response = await fetch(
        (window.routes && window.routes.cart_add_url) || form.action || '/cart/add.js',
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
          credentials: 'same-origin',
        }
      );
      var data = await response.json();

      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || 'This item could not be added to your bag.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sm-cart-drawer-guard',
          productVariantId: formData.get('id'),
          cartData: data,
        });
      }

      if (!await refreshAndOpen(data, submitter)) { window.location.assign((window.Shopify?.routes?.root || '/') + 'cart'); }
    } catch (error) {
      showFormError(form, (error && error.message) || 'This item could not be added to your bag.');
    } finally {
      setLoading(submitter, false);
    }
  }

  window.SaleMadridCartDrawer = {
    getDrawer: getDrawer,
    fetchFreshSections: fetchFreshSections,
    open: openDrawer,
    refreshAndOpen: refreshAndOpen,
  };

  /* Bubble phase is intentional: the theme's component listener on the form
     gets the first opportunity to handle the request. */
  document.addEventListener('submit', handleUnhandledCartForm, false);
})();
